import React, { useState, useRef, useEffect } from "react";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import Button from "../shared/Button";
import DownloadIcon from "../../assets/icons/DownloadIcon";
import {
  ShortfyEvent,
  formatEventDate,
} from "../../types/event.types";
//import { exportEventToPDF } from "../utils/exportEventToPDF";
import { exportEventToCSVReport } from "../utils/exportEventToCSV";
import { exportEventToXLSX } from "../utils/exportEventToXLSX";
import { downloadQRCode } from "../utils/qrUtils";
import shortfyLogoPng from "../../assets/images/Shortfy-Logo.png";
import "./EventCard.css";
import CardHeader, { MenuItem } from "../shared/cards/CardHeader";
import BaseCard from "../shared/cards/BaseCard";
import CardContent from "../shared/cards/CardContent";
import { useNotification } from "../../context/NotificationContext";
import { useClientPrincipal } from "../../context/ClientPrincipalContext"; // NEW: for role-based export access
import { EventStatus } from "../../types/shared.types";
import { getRegistrationStats } from "../../hooks/useRegistration";
import { RegistrationStats } from "../../types/registration.types";

interface EventCardProps {
  event: ShortfyEvent;
  onEdit?: (event: ShortfyEvent) => void;
  onDelete?: (event: ShortfyEvent) => void;
  onCancel?: (event: ShortfyEvent) => void;
  onComplete?: (event: ShortfyEvent) => void;
  onCopyUrl?: (url: string) => void;
  defaultExpanded?: boolean;
}

const STATUS_COLORS: Record<EventStatus, string> = {
  [EventStatus.Active]: "#1ab977",
  [EventStatus.Cancelled]: "#b00020",
  [EventStatus.Completed]: "#767676",
};

const formatDuration = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationMs = end.getTime() - start.getTime();
  const durationMinutes = Math.floor(durationMs / (1000 * 60));
  const durationHours = Math.floor(durationMinutes / 60);
  const durationDays = Math.floor(durationHours / 24);

  if (durationDays > 0) {
    const remainingHours = durationHours % 24;
    return remainingHours > 0
      ? `${durationDays}d ${remainingHours}h`
      : `${durationDays} day${durationDays > 1 ? "s" : ""}`;
  }
  if (durationHours > 0) {
    const remainingMinutes = durationMinutes % 60;
    return remainingMinutes > 0
      ? `${durationHours}h ${remainingMinutes}m`
      : `${durationHours} hour${durationHours > 1 ? "s" : ""}`;
  }
  return `${durationMinutes} minute${durationMinutes > 1 ? "s" : ""}`;
};

const EventCard: React.FC<EventCardProps> = ({
  event,
  onEdit,
  onDelete,
  onCancel,
  onComplete,
  onCopyUrl,
  defaultExpanded = false,
}) => {
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);
  const { setNotification } = useNotification();
  const { clientPrincipal } = useClientPrincipal(); // NEW
  const currentUserId = clientPrincipal?.userId ?? "";
  const userRoles = clientPrincipal?.userRoles ?? [];
  // NEW: allow export if user is EVENT_ADMIN or the event creator
  const canExport = userRoles.includes("EVENT_ADMIN") || event.createdBy === currentUserId;

  const [participantStats, setParticipantStats] = useState<RegistrationStats | null>(null);

  useEffect(() => {
    if (!event.additionalGuestsEnabled || !canExport) return;
    getRegistrationStats(event.eventId).then((res) => {
      if (res.success && res.data) setParticipantStats(res.data);
    });
  }, [event.eventId, event.additionalGuestsEnabled, canExport]);

  const currentRegs = event.currentRegistrations ?? 0;
  const isPastEvent = new Date(event.eventStartDate).getTime() < Date.now();
  const effectiveStatus =
    event.status === EventStatus.Active && isPastEvent
      ? EventStatus.Completed
      : event.status;
  const isActive = effectiveStatus === EventStatus.Active;

  const backendUrlRaw = process.env.REACT_APP_BACKEND_URL || "";
  // If REACT_APP_BACKEND_URL is an absolute URL (local dev: "http://localhost:5000"), use it directly.
  // If it's a relative path ("/api") or unset (production SWA), fall back to window.location.origin
  // so the short URL is always absolute and scannable (e.g. https://shortfy.it/api/go/CGI-xxx).
  const backendBase = backendUrlRaw.startsWith("http")
    ? backendUrlRaw.replace(/\/$/, "")
    : window.location.origin;
  const shortUrl = event.registrationShortCode
    ? `${backendBase}/api/go/${event.registrationShortCode}`
    : event.registrationShortUrl || "";

  const capacityPercent = (currentRegs / event.maxCapacity) * 100;
  const isFull = currentRegs >= event.maxCapacity;

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        downloadRef.current &&
        !downloadRef.current.contains(e.target as Node)
      ) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [downloadMenuOpen]);

  const handleDownloadQR = async (format: "png" | "svg") => {
    const fileName = `${event.eventName.replace(/[^a-z0-9]/gi, "_")}_qr`;

    await downloadQRCode({
      fileName,
      format,
      qrCodeElementId: `qr-code-${event.eventId}`,
      logoUrl: "/images/CGI-Logo.jpg",
      withLogo: true,
      includeDisclaimer: true,
      shortfyLogoUrl: shortfyLogoPng,
    });
  };

  const menuItems: MenuItem[] = [
   // { label: "Export PDF", onClick: () => exportEventToPDF(event) },
    // { label: "Export CSV", onClick: () => exportEventToCSVReport(event) }, // OLD
    // { label: "Export XLSX", onClick: () => exportEventToXLSX(event) },    // OLD
    {
      label: "Export Participants",
      show: canExport, // NEW: only EVENT_ADMIN or event creator can export
      children: [
        { label: "XLSX", onClick: () => exportEventToXLSX(event) },
        { label: "CSV", onClick: () => exportEventToCSVReport(event) },
      ],
    },
    {
      label: "Edit",
      onClick: () => onEdit?.(event),
      show: isActive && !isPastEvent && !!onEdit,
    },
    {
      label: "Cancel Event",
      onClick: () => onCancel?.(event),
      variant: "danger",
      show: isActive && !isPastEvent && !!onCancel,
    },
    {
      label: "Mark Complete",
      onClick: () => onComplete?.(event),
      show: isActive && isPastEvent && !!onComplete,
    },
    {
      label: "Delete",
      onClick: () => onDelete?.(event),
      variant: "danger",
      show: currentRegs === 0 && !!onDelete,
    },
  ];

  const formatUrl = (url: string) => url.replace(/^https?:\/\/(api\.)?/, "");

  return (
    <BaseCard
      id={event.eventId}
      defaultExpanded={defaultExpanded}
      renderHeader={({ isExpanded, onToggle }) => (
        <CardHeader
          title={event.eventName}
          isExpanded={isExpanded}
          onToggle={onToggle}
          statusBadge={
            <span
              className="event-status-badge"
              style={{ backgroundColor: STATUS_COLORS[effectiveStatus] }}
            >
              {effectiveStatus}
            </span>
          }
          menuItems={menuItems}
        />
      )}
    >
      <CardContent
        qrSection={
          <div className="qr-image" id={`qr-code-${event.eventId}`}>
            <QRCodeRenderer
              value={shortUrl}
              size={200}
              withLogo
              logoUrl="/images/CGI-Logo.jpg"
            />
          </div>
        }
        infoSection={
          <>
            <div className="event-card-lines">
              <div>
                <span className="label">Date:</span>
                <span className="info-value">
                  {formatEventDate(event.eventStartDate)} –{" "}
                  {formatEventDate(event.eventEndDate)} (
                  {formatDuration(event.eventStartDate, event.eventEndDate)})
                </span>
              </div>
              <div>
                <span className="label">Location:</span>
                <span className="info-value">{event.location}</span>
              </div>
              {event.registrationShortUrl && (
                <div>
                  <span className="label">Registration URL:</span>
                  <a
                    className="short-url"
                    href={shortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {formatUrl(shortUrl)}
                  </a>
                </div>
              )}
              {event.registrationDeadline && (
                <div>
                  <span className="label">Deadline:</span>
                  <span className="info-value">
                    {new Date(event.registrationDeadline).toLocaleDateString(
                      "de-DE",
                    )}
                  </span>
                </div>
              )}
              <div className="capacity-line">
                <span className="label">Registrations:</span>
                <span className="info-value">
                  {currentRegs} / {event.maxCapacity}
                  {isFull && <span className="full-badge">Full</span>}
                </span>
              </div>
              <div className="capacity-bar-container">
                <div className="capacity-bar">
                  <div
                    className={`capacity-fill ${isFull ? "full" : ""}`}
                    style={{ width: `${Math.min(capacityPercent, 100)}%` }}
                  />
                </div>
              </div>
              {event.mealOptionsEnabled && (
                <div>
                  <span className="label">Meal Options:</span>
                  <span className="info-value enabled">Enabled</span>
                </div>
              )}
              {event.additionalGuestsEnabled && (
                <>
                  <div>
                    <span className="label">Additional Guests:</span>
                    <span className="info-value enabled">Enabled</span>
                  </div>
                  {participantStats && (
                    <div className="participant-stats">
                      <span className="label">Total Participants:</span>
                      <span className="info-value">
                        {participantStats.totalParticipants}
                        {" "}({currentRegs} partner{currentRegs !== 1 ? "s" : ""}
                        {participantStats.totalAdults > 0 && ` + ${participantStats.totalAdults} adult${participantStats.totalAdults !== 1 ? "s" : ""}`}
                        {participantStats.totalChildren > 0 && ` + ${participantStats.totalChildren} child${participantStats.totalChildren !== 1 ? "ren" : ""}`})
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="event-card-btns">
              <div className="download-btn-wrapper" ref={downloadRef}>
                <Button
                  className="event-card-btn"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDownloadMenuOpen((o) => !o);
                  }}
                >
                  Download QR
                  <span
                    className="download-svg-icon"
                    style={{ marginLeft: "6px" }}
                  >
                    <DownloadIcon />
                  </span>
                </Button>
                {downloadMenuOpen && (
                  <div className="download-dropdown-horizontal">
                    <button
                      className="download-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadQR("png");
                        setDownloadMenuOpen(false);
                      }}
                    >
                      PNG
                    </button>
                    <button
                      className="download-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadQR("svg");
                        setDownloadMenuOpen(false);
                      }}
                    >
                      SVG
                    </button>
                  </div>
                )}
              </div>
              <Button
                className="event-card-btn"
                variant="primary"
                onClick={() => {
                  navigator.clipboard.writeText(shortUrl);
                  setNotification({
                    type: "success",
                    message: "URL copied to clipboard!",
                  });
                  onCopyUrl?.(shortUrl);
                }}
              >
                Copy URL
              </Button>
            </div>
          </>
        }
      />
    </BaseCard>
  );
};

export default EventCard;
